import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

export interface IMetricChipProps {
  /** Right-aligned label. Empty renders an invisible placeholder that still holds the column width. */
  label: string;
  /** Right-aligned value. */
  value: string;
  /** Extra class on the value sub-cell (e.g. a warn tone). */
  valueClass?: string;
  /** Fixed width of the label cell. Default fits the thread strip's short labels (`seq`, `Δ`, `lat`). */
  labelWidthClass?: string;
  /** Fixed width of the value cell. Default fits the thread strip's durations; widen for large token counts. */
  valueWidthClass?: string;
}

/**
 * One label/value cell rendered as a pair of fixed-width sub-cells: label + value
 * both right-aligned. Shared by the thread transcript's per-row metadata strips
 * and the benchmark transcript's per-round stat line — the fixed widths guarantee
 * every chip's value right-edge lands at the same screen-X regardless of row
 * type, so columns align down a dense transcript. Pass empty strings to render an
 * invisible placeholder cell that still consumes column width, keeping later
 * chips aligned when the chip set differs between contexts. The width defaults
 * suit the thread strip; the benchmark stat line widens both for its longer
 * labels (`cache`, `think`) and larger values (six-figure token counts).
 */
const MetricChip = ({
  label,
  value,
  valueClass = '',
  labelWidthClass = 'w-[3ch]',
  valueWidthClass = 'w-[7ch]',
}: IMetricChipProps): React.ReactElement => (
  // `shrink-0` on the wrapper AND each sub-span stops flex from collapsing
  // empty-content cells; without it, placeholder cells shrink to zero and the
  // populated chips drift left of their per-row siblings, breaking alignment.
  <span className="flex shrink-0 items-baseline gap-1.5">
    <span className={cx('text-fg-quaternary shrink-0 text-right', labelWidthClass)}>{label}</span>
    <span className={cx('shrink-0 text-right', valueWidthClass, valueClass)}>{value}</span>
  </span>
);

export default MetricChip;
